import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronDown,
  ListChecks,
  Mail,
  MessageSquareText,
  Phone,
  Plus,
  ScrollText,
  ExternalLink,
  Trash2,
  Undo2,
  UploadCloud,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';

import PickerMenu from './PickerMenu';
import TimeField from './TimeField';
import { FILL_INS, fill, pickWording } from '../../../lib/messageCore';
import {
  cloneDefinition,
  renumberSteps,
  normalizeChains,
  stepWindowSummary,
  newStep,
  groupStepsByDay,
  diffDefinitions,
} from './cadenceEditorUtils';
import {
  useFetchCadenceVersions,
  useFetchCadenceVersion,
  useCreateCadenceDraft,
  useSaveCadenceDraft,
  useDeleteCadenceDraft,
  useSubmitCadenceForApproval,
  useWithdrawCadenceSubmission,
  usePublishCadenceVersion,
  useFetchCadenceScripts,
} from '../../../services/onboardingBoard';

// Manager-only cadence editor (Phase 4). Drafts are the ONLY mutable state:
// published versions (active or historical) are immutable, and edits ship by
// publishing a draft, which becomes the new active version. In-flight leads
// keep the version they were taken under.
//
// Rendered as a full-screen OVERLAY over the board (same pattern as the
// Schedule calendar; Nic 2026-08-09 — the manager tools live in the board's
// settings gear, not as standalone pages).

// Dark variants follow the board/calendar token language (surface-page /
// surface-card / surface-elevated; `dark:!bg-…` beats Bootstrap's
// !important utilities). The board applies `dark` on <html> while themed,
// so the portaled overlay inherits it.
const CARD =
  'rounded-xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';
const INPUT =
  'rounded border border-slate-200 bg-transparent px-1.5 py-0.5 text-[12px] font-medium text-slate-700 focus:border-slate-400 focus:outline-none disabled:opacity-50 dark:border-white/10 dark:text-slate-200 dark:focus:border-white/30';
const PICKER_TRIGGER =
  'rounded border border-slate-200 px-1.5 py-0.5 text-[12px] font-medium text-slate-700 hover:border-slate-300 dark:border-white/10 dark:text-slate-200 dark:hover:border-white/20';

const STEP_TYPE_META = {
  call: {
    label: 'Call',
    Icon: Phone,
    chip: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400',
  },
  sms: {
    // Green, not violet (Nic 2026-08-14): violet is the AUTOMATION color on
    // this screen (the lightning badge + automated-step outline), so a text
    // step wearing it read as "this sends itself" when texts can never
    // automate.
    label: 'Text',
    Icon: MessageSquareText,
    chip: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
  },
  email: {
    label: 'Email',
    Icon: Mail,
    chip: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
  },
};

// A step type's message kind.
const STEP_CHANNEL = { sms: 'text', email: 'email', call: 'call' };

const STATUS_LABEL = {
  active: 'Active',
  draft: 'Draft',
  pending_approval: 'Pending approval',
  historical: 'Historical',
};

const Toggle = ({ label, value, onChange, disabled, title, onClassName }) => (
  <button
    type="button"
    disabled={disabled}
    title={title}
    onClick={() => onChange(!value)}
    className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-medium transition-colors disabled:opacity-50 ${
      value
        ? onClassName ||
          'bg-slate-800 text-white dark:bg-slate-200 dark:!text-slate-900'
        : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-400 dark:hover:bg-white/15'
    }`}
  >
    {value ? <Check size={12} /> : null}
    {label}
  </button>
);

// Validation readout — the server's verdict from the last save/fetch.
// Errors block publish; warnings never do.
const IssuesBar = ({ validation, dirty }) => {
  const [open, setOpen] = useState(true);
  const errors = validation?.errors || [];
  const warnings = validation?.warnings || [];
  if (errors.length === 0 && warnings.length === 0) {
    return (
      <div className="mb-3 flex items-center gap-2 rounded-lg bg-emerald-50/70 px-3 py-1.5 text-[12px] font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
        <Check size={12} />
        No validation issues{dirty ? ' (as of the last save)' : ''}.
      </div>
    );
  }
  return (
    <div
      className={`mb-3 rounded-lg px-3 py-2 text-[12px] ${
        errors.length > 0
          ? 'bg-rose-50/80 dark:bg-rose-500/10'
          : 'bg-amber-50/80 dark:bg-amber-500/10'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-left font-semibold"
      >
        <AlertTriangle
          size={12}
          className={errors.length > 0 ? 'text-rose-500' : 'text-amber-500'}
        />
        <span
          className={
            errors.length > 0
              ? 'text-rose-600 dark:text-rose-400'
              : 'text-amber-600 dark:text-amber-400'
          }
        >
          {errors.length > 0
            ? `${errors.length} error${errors.length === 1 ? '' : 's'} blocking publish`
            : `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
          {errors.length > 0 && warnings.length > 0
            ? ` · ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`
            : ''}
          {dirty ? ' (as of the last save)' : ''}
        </span>
        <ChevronDown
          size={12}
          className={`ml-auto text-slate-400 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <ul className="mt-1.5 space-y-0.5 pl-5">
          {errors.map((msg) => (
            <li
              key={msg}
              className="list-disc text-rose-600 dark:text-rose-400"
            >
              {msg}
            </li>
          ))}
          {warnings.map((msg) => (
            <li
              key={msg}
              className="list-disc text-amber-600 dark:text-amber-400"
            >
              {msg}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

// One step: collapsed summary row; expands into the full editor.
const StepRow = ({
  step,
  steps,
  scripts,
  editable,
  expanded,
  onToggle,
  onChange,
  onMove,
  onRemove,
}) => {
  const meta = STEP_TYPE_META[step.type] || STEP_TYPE_META.call;
  const { Icon } = meta;
  // Chains order a single day's sequence — only same-day earlier steps are
  // offered (cross-day chains are an engine trap: see cadenceValidation.js).
  const earlier = steps.filter(
    (s) => s.stepNumber < step.stepNumber && s.day === step.day,
  );
  // Only messages of the step's kind (Messages, 2026-10-22).
  const channel = STEP_CHANNEL[step.type] || 'text';
  const scriptOptions = [
    { value: null, label: 'No message' },
    ...Object.entries(scripts)
      .filter(([key, s]) => key === step.scriptKey || (s.channel || 'text') === channel)
      .map(([key, s]) => ({ value: key, label: s.label || key })),
  ];
  const picked = step.scriptKey ? scripts[step.scriptKey] : null;

  // ONE timing truth per step (Nic 2026-08-09): the Window picker is the
  // only timing control. 'chain' ("after an earlier step") IS the chain —
  // it gates the order AND re-times off the dependency's actual completion,
  // always; the provisional plan derives server-side. No separate
  // chain/re-time controls exist.
  const clearTimingFields = (s) => {
    delete s.offsetStartMinutes;
    delete s.offsetEndMinutes;
    delete s.windowStartLocal;
    delete s.windowEndLocal;
    delete s.chainAfter;
    delete s.chainOffsetStartMinutes;
    delete s.chainOffsetEndMinutes;
  };
  const windowKindLabel =
    step.anchor === 'arrival'
      ? 'Minutes after arrival'
      : step.anchor === 'chain'
        ? 'After the previous step'
        : 'Clock window that day';

  // Automation is email-only, so this is the one step type that can wear it.
  // A violet inner border carries it alone (Nic 2026-08-14 — a badge on the
  // icon was too loud). Outline, not ring/border: CARD already owns `ring` in
  // dark mode and `shadow`, and an outline with a negative offset paints
  // inside the card without fighting either.
  const automated = step.type === 'email' && Boolean(step.autoSend);

  return (
    <div
      className={`${CARD} overflow-visible ${
        automated
          ? 'outline outline-2 -outline-offset-2 outline-violet-400 dark:outline-violet-400/70'
          : ''
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left"
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${meta.chip}`}
        >
          <Icon size={12} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
            {step.label || step.stepKey}
          </span>
          <span className="block truncate text-[12px] text-slate-400 dark:text-slate-500">
            {stepWindowSummary(step, steps)}
            {step.morningLeadOnly ? ' · morning leads' : ''}
            {step.leaveVoicemail ? ' · leaves VM' : ''}
          </span>
        </span>
        <span className="shrink-0 font-mono text-[11px] text-slate-300 dark:text-slate-600">
          {step.stepKey}
        </span>
        <ChevronDown
          size={13}
          className={`shrink-0 text-slate-400 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded ? (
        <div className="space-y-2.5 border-t border-slate-100 dark:border-white/10 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
              Label
              <input
                type="text"
                disabled={!editable}
                value={step.label || ''}
                onChange={(e) =>
                  onChange((s) => {
                    s.label = e.target.value;
                  })
                }
                className={`${INPUT} w-36`}
              />
            </label>
            <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
              Type
              <span className="w-20">
                <PickerMenu
                  label={meta.label}
                  disabled={!editable}
                  triggerClassName={PICKER_TRIGGER}
                  options={Object.entries(STEP_TYPE_META).map(([value, m]) => ({
                    value,
                    label: m.label,
                  }))}
                  onPick={(value) =>
                    onChange((s) => {
                      s.type = value;
                      // Voicemail is a call concept; a plain call reads no
                      // script (the Script row hides — don't carry a stale
                      // pick into it).
                      if (value !== 'call') s.leaveVoicemail = false;
                      else if (!s.leaveVoicemail) s.scriptKey = null;
                      // Automation is EMAIL-ONLY — flipping away from email
                      // must not carry the flag into validation.
                      if (value !== 'email') delete s.autoSend;
                    })
                  }
                />
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
              Window
              {/* The picker shows only the kinds that make sense here:
                  "Minutes after arrival" is day-1-only (the opening burst);
                  "After an earlier step" needs an earlier same-day step.
                  When only one kind is possible the dropdown is hidden. */}
              {step.day === 1 || earlier.length > 0 ? (
                <span className="w-40">
                  <PickerMenu
                    label={windowKindLabel}
                    disabled={!editable}
                    triggerClassName={PICKER_TRIGGER}
                    options={[
                      ...(step.day === 1
                        ? [{ value: 'arrival', label: 'Minutes after arrival' }]
                        : []),
                      { value: 'day', label: 'Clock window that day' },
                      ...(earlier.length > 0
                        ? [{ value: 'chain', label: 'After the previous step' }]
                        : []),
                    ]}
                    onPick={(anchor) =>
                      onChange((s) => {
                        s.anchor = anchor;
                        clearTimingFields(s);
                        if (anchor === 'arrival') {
                          s.day = 1;
                          s.offsetStartMinutes = 1;
                          s.offsetEndMinutes = 15;
                        } else if (anchor === 'chain') {
                          // Default: follow the nearest earlier step.
                          s.chainAfter = earlier[earlier.length - 1].stepKey;
                          s.chainOffsetStartMinutes = 20;
                          s.chainOffsetEndMinutes = 30;
                        } else {
                          s.windowStartLocal = '08:00';
                          s.windowEndLocal = '10:00';
                        }
                      })
                    }
                  />
                </span>
              ) : null}
            </label>
            {step.anchor === 'arrival' ? (
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                <input
                  type="number"
                  min={0}
                  disabled={!editable}
                  value={step.offsetStartMinutes ?? ''}
                  onChange={(e) =>
                    onChange((s) => {
                      s.offsetStartMinutes = parseInt(e.target.value, 10) || 0;
                    })
                  }
                  className={`${INPUT} w-14`}
                />
                to
                <input
                  type="number"
                  min={0}
                  disabled={!editable}
                  value={step.offsetEndMinutes ?? ''}
                  onChange={(e) =>
                    onChange((s) => {
                      s.offsetEndMinutes = parseInt(e.target.value, 10) || 0;
                    })
                  }
                  className={`${INPUT} w-14`}
                />
                min
              </span>
            ) : step.anchor === 'chain' ? (
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                <input
                  type="number"
                  min={0}
                  disabled={!editable}
                  value={step.chainOffsetStartMinutes ?? ''}
                  onChange={(e) =>
                    onChange((s) => {
                      s.chainOffsetStartMinutes =
                        parseInt(e.target.value, 10) || 0;
                    })
                  }
                  className={`${INPUT} w-14`}
                />
                to
                <input
                  type="number"
                  min={0}
                  disabled={!editable}
                  value={step.chainOffsetEndMinutes ?? ''}
                  onChange={(e) =>
                    onChange((s) => {
                      s.chainOffsetEndMinutes =
                        parseInt(e.target.value, 10) || 0;
                    })
                  }
                  className={`${INPUT} w-14`}
                />
                min
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                <TimeField
                  disabled={!editable}
                  value={step.windowStartLocal}
                  onChange={(v) =>
                    onChange((s) => {
                      s.windowStartLocal = v;
                    })
                  }
                />
                to
                <TimeField
                  disabled={!editable}
                  value={step.windowEndLocal}
                  onChange={(v) =>
                    onChange((s) => {
                      s.windowEndLocal = v;
                    })
                  }
                />
              </span>
            )}
          </div>

          {/* Options: voicemail is a call thing, morning-leads is a DAY-1
              thing (it keys off whether the lead ARRIVED before noon —
              meaningless from day 2 on), and Send automatically is an
              sms/email thing (calls always need a human). Every type has at
              least one, so the row always renders. */}
          {/* Options: voicemail = calls, morning-leads = day 1, automation =
              EMAIL ONLY (identity model 2026-08-10: texts and calls always
              come from a person; automated emails send as Veritas Ratings). */}
          {step.type === 'call' || step.type === 'email' || step.day === 1 ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
                Options
              </span>
              {step.type === 'call' ? (
                <Toggle
                  label="Leave voicemail"
                  value={Boolean(step.leaveVoicemail)}
                  disabled={!editable}
                  onChange={(v) =>
                    onChange((s) => {
                      s.leaveVoicemail = v;
                      // No voicemail = no script to read — clear it so the
                      // hidden Script row never carries a stale pick.
                      if (!v) s.scriptKey = null;
                    })
                  }
                />
              ) : null}
              {step.type === 'email' ? (
                <Toggle
                  label="Send automatically"
                  value={Boolean(step.autoSend)}
                  disabled={!editable}
                  title="Goes out on its own when the window opens. Shows purple on the board and never counts toward anyone's SLAs."
                  onClassName="bg-violet-600 text-white dark:bg-violet-500 dark:!text-white"
                  onChange={(v) =>
                    onChange((s) => {
                      if (v) s.autoSend = true;
                      else delete s.autoSend;
                    })
                  }
                />
              ) : null}
              {step.day === 1 ? (
                <Toggle
                  label="Morning leads only"
                  value={Boolean(step.morningLeadOnly)}
                  disabled={!editable}
                  title="Only materializes for leads that arrived before noon."
                  onChange={(v) =>
                    onChange((s) => {
                      s.morningLeadOnly = v;
                    })
                  }
                />
              ) : null}
              {/* lastTry deliberately NOT surfaced (Nic 2026-08-09): the
                  flag is inert metadata today (no consumer) — the toggle
                  only confused. Existing values (Call #10) pass through
                  saves untouched; re-add a control here if a "final
                  attempt" treatment ever ships. */}
            </div>
          ) : null}
          {step.type === 'email' && step.autoSend ? (
            <div
              className="text-[11px] font-medium text-violet-600 dark:text-violet-400"
              title={
                'Automated messages speak for the company and should not pretend to be a person. Example: "Good morning! Onboarding specialist [support first name] will be reaching out to you this morning. We look forward to hearing from you. - Veritas Ratings"'
              }
            >
              Sends from Veritas Ratings, not from a person. Hover for an
              example.
            </div>
          ) : null}

          {/* A message only when one is actually read/sent: always for SMS
              and email; for calls only when leaving a voicemail. The words
              are written on the Messages page. */}
          {step.type !== 'call' || step.leaveVoicemail ? (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                Message
                <span className="w-56">
                  <PickerMenu
                    label={
                      step.scriptKey
                        ? scripts[step.scriptKey]?.label || step.scriptKey
                        : 'No message'
                    }
                    disabled={!editable}
                    triggerClassName={PICKER_TRIGGER}
                    options={scriptOptions}
                    onPick={(value) =>
                      onChange((s) => {
                        s.scriptKey = value;
                      })
                    }
                  />
                </span>
              </label>
              {picked ? <MessagePeek script={picked} /> : null}
            </div>
          ) : null}

          {editable ? (
            <div className="flex items-center gap-1 border-t border-slate-100 dark:border-white/10 pt-2">
              <button
                type="button"
                onClick={() => onMove(-1)}
                title="Move earlier"
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-300"
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => onMove(1)}
                title="Move later"
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-300"
              >
                <ArrowDown size={13} />
              </button>
              <button
                type="button"
                title="Removing a day's last step leaves it as a quiet day."
                onClick={onRemove}
                className="ml-auto flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-rose-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
              >
                <Trash2 size={12} /> Remove step
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// A step's message, as a partner's lead reads it (sample names), and how many
// wordings it has. Every wording is on the Messages page.
const SAMPLES = Object.fromEntries(FILL_INS.map((f) => [f.key, f.sample]));
const MessagePeek = ({ script }) => {
  const read = useMemo(() => {
    const message = script.message || { mode: 'one', wordings: { all: { subject: script.subject, body: script.body } } };
    const w = pickWording(message, { kind: 'partner', id: null, name: SAMPLES.partner_name }) || { body: script.body };
    const values = { ...SAMPLES, source_name: SAMPLES.partner_name };
    return { subject: w.subject ? fill(w.subject, values) : null, body: fill(w.body || '', values) };
  }, [script]);
  const bySource = script.message?.mode === 'by_source';
  return (
    <div className="rounded-lg bg-slate-50 px-2.5 py-2 text-[12px] leading-relaxed text-slate-600 ring-1 ring-slate-100 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10">
      {read.subject ? <div className="font-semibold text-slate-700 dark:text-slate-200">{read.subject}</div> : null}
      <div className="line-clamp-3 whitespace-pre-wrap break-words">{read.body || 'No words yet.'}</div>
      <div className="mt-1 text-[11px] text-slate-400">
        {bySource ? 'Worded for where each lead came from. This is how a partner\'s lead reads it.' : 'The same words for everyone.'}
      </div>
    </div>
  );
};

// The words the steps send are written on the Messages page.
const MessagesCard = () => (
  <div className={`${CARD} space-y-1.5 p-3`}>
    <div className="flex items-center gap-1.5 text-[13px] font-bold text-slate-700 dark:text-slate-200">
      <ScrollText size={13} className="text-slate-400" />
      Messages
    </div>
    <div className="text-[12px] leading-relaxed text-slate-500 dark:text-slate-400">
      Each step sends a message: pick it on the step. Write the words, worded for where each lead came from (a
      partner, an event, our website, a referral, a call), on the Messages page. Saved words go out right away, without
      a publish.
    </div>
    <Link
      to="/operations/messages"
      className="inline-flex items-center gap-1 text-[12px] font-semibold text-sky-600 no-underline hover:underline dark:text-sky-400"
    >
      Write messages on the Messages page <ExternalLink size={11} />
    </Link>
  </div>
);

// Working hours / weekend rules DELIBERATELY absent (Nic 2026-08-09): they
// looked like they controlled far more than they did. Working time is the
// org work calendar, managed on the Schedule calendar; the
// cadence follows it automatically.
const VersionCard = ({ label, versionNumber, timezone, editable, onLabel }) => (
  <div className={`${CARD} space-y-2 p-3`}>
    <div className="flex items-center gap-1.5 text-[13px] font-bold text-slate-700 dark:text-slate-200">
      <CalendarClock size={13} className="text-slate-400" />
      Version{versionNumber ? ` v${versionNumber}` : ''}
    </div>
    <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
      Name
      <input
        type="text"
        disabled={!editable}
        value={label}
        onChange={(e) => onLabel(e.target.value)}
        className={`${INPUT} flex-1`}
      />
    </label>
    <div className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500">
      Timezone: {timezone}. Working hours and days off live on the Schedule
      calendar; the cadence follows them automatically.
    </div>
  </div>
);

// Confirmation for both halves of the approval gate (2026-08-15): the same
// diff vs the current active version, worded for whoever is acting. Scripts
// never appear here — they are live (2026-08-11) and publish independently
// of any version.
const CONFIRM_COPY = {
  submit: {
    title: (label) => `Send “${label}” for approval?`,
    body: 'This locks the draft and emails the approver. Nothing goes live until they approve it. Withdraw to edit again.',
    cta: 'Send for Approval',
  },
  publish: {
    title: (label) => `Publish “${label}”?`,
    body: 'This becomes the live cadence for every NEW take. Leads already in flight keep the version they started under.',
    cta: 'Publish',
  },
};

const PublishConfirm = ({
  diff,
  label,
  busy,
  mode = 'publish',
  onConfirm,
  onCancel,
}) => {
  const copy = CONFIRM_COPY[mode] || CONFIRM_COPY.publish;
  const lines = [
    diff.stepsAdded.length &&
      `${diff.stepsAdded.length} step${diff.stepsAdded.length === 1 ? '' : 's'} added (${diff.stepsAdded.join(', ')})`,
    diff.stepsRemoved.length &&
      `${diff.stepsRemoved.length} step${diff.stepsRemoved.length === 1 ? '' : 's'} removed (${diff.stepsRemoved.join(', ')})`,
    diff.stepsChanged.length &&
      `${diff.stepsChanged.length} step${diff.stepsChanged.length === 1 ? '' : 's'} changed`,
    diff.hoursChanged && 'Business hours / weekend rule changed',
  ].filter(Boolean);

  return createPortal(
    <div className="fixed inset-0 z-[1080] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/30 dark:bg-black/60"
        aria-hidden
      />
      <div className="relative m-4 w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
        <div className="mb-1 text-sm font-bold text-slate-800 dark:text-slate-100">
          {copy.title(label)}
        </div>
        <div className="mb-2 text-[13px] text-slate-400">{copy.body}</div>
        {lines.length > 0 ? (
          <ul className="mb-3 space-y-0.5 pl-4 text-[12px] text-slate-500 dark:text-slate-400">
            {lines.map((line) => (
              <li key={line} className="list-disc">
                {line}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mb-3 text-[12px] text-slate-400">
            Identical to the current active version.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-white"
          >
            <UploadCloud size={13} /> {copy.cta}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * canEdit  = onboarding_cadence:edit    — build, save, submit, withdraw
 * canApprove = onboarding_cadence:approve — approve a submitted version
 *
 * Someone can hold either alone. An approve-only reviewer opens the editor
 * read-only, which is why the overlay mounts on EITHER permission.
 */
// asPage (Nic 2026-10-23): Admin > Cadence Editor is its own page, so the
// editor renders in place (no backdrop, no close, no Escape).
const CadenceEditorOverlay = ({ open, onClose, canEdit, canApprove, asPage = false }) => {
  const queryClient = useQueryClient();

  const versionsQ = useFetchCadenceVersions({ enabled: open });
  const versions = useMemo(
    () => versionsQ.data?.versions || [],
    [versionsQ.data],
  );
  const activeVersion = versions.find((v) => v.isActive);

  // The messages a step can send (live, off the versions: one truth for
  // every version, draft, and run; written on the Messages page).
  const scriptsQ = useFetchCadenceScripts({ enabled: open });
  const liveScripts = scriptsQ.data?.scripts || {};

  const [selectedId, setSelectedId] = useState(null);
  const effectiveId = selectedId || activeVersion?.cadenceVersionId || null;
  const detailQ = useFetchCadenceVersion(effectiveId, {
    enabled: open && !!effectiveId,
  });
  const serverVersion = detailQ.data?.version || null;
  const activeDetailQ = useFetchCadenceVersion(
    activeVersion?.cadenceVersionId || null,
    { enabled: open && !!activeVersion },
  );

  // Local working copy — reset whenever a different version arrives.
  const [working, setWorking] = useState(null); // { label, definition }
  const [dirty, setDirty] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  useEffect(() => {
    if (!serverVersion) return;
    setWorking({
      label: serverVersion.label,
      definition: cloneDefinition(serverVersion.definition),
    });
    setDirty(false);
  }, [serverVersion]);

  useEffect(() => {
    if (!open || asPage) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, asPage]);

  // A pending version is read-only for everyone: the approver publishes
  // exactly what they were shown, and the manager withdraws to change it.
  const pending = serverVersion?.status === 'pending_approval';
  const editable = serverVersion?.status === 'draft' && canEdit;
  const validation = serverVersion?.validation || { errors: [], warnings: [] };

  const refresh = () => queryClient.invalidateQueries(['cadence-versions']);
  const onError = (err) =>
    toast.error(err?.response?.data?.msg || 'That didn’t go through.');

  const createMut = useCreateCadenceDraft(
    (data) => {
      refresh();
      setSelectedId(data.version.cadenceVersionId);
      toast.success('Draft created.');
    },
    { onError },
  );
  const saveMut = useSaveCadenceDraft(
    () => {
      refresh();
      toast.success('Draft saved.');
    },
    { onError },
  );
  const deleteMut = useDeleteCadenceDraft(
    (_data, variables) => {
      // Drop the deleted version's detail query BEFORE invalidating. The list
      // (['cadence-versions']) is a KEY PREFIX of every per-version detail
      // (['cadence-versions', id]), so a plain invalidate refetches the row we
      // just deleted and answers 404 in the console. Removing it first is the
      // fix; the view then falls back to the active version on its own.
      if (variables?.cadenceVersionId) {
        queryClient.removeQueries([
          'cadence-versions',
          variables.cadenceVersionId,
        ]);
      }
      setSelectedId(null);
      refresh();
      toast.success('Draft deleted.');
    },
    { onError },
  );
  const submitMut = useSubmitCadenceForApproval(
    () => {
      refresh();
      setConfirmPublish(false);
      toast.success('Sent for approval.');
    },
    {
      onError: (err) => {
        setConfirmPublish(false);
        const v = err?.response?.data?.validation;
        toast.error(
          v?.errors?.length
            ? `Blocked: ${v.errors[0]}`
            : err?.response?.data?.msg || 'Could not send for approval.',
        );
        refresh();
      },
    },
  );
  const withdrawMut = useWithdrawCadenceSubmission(
    () => {
      refresh();
      toast.success('Withdrawn. The draft is editable again.');
    },
    { onError },
  );
  const publishMut = usePublishCadenceVersion(
    () => {
      refresh();
      setConfirmPublish(false);
      toast.success('Published. New takes use this version.');
    },
    {
      onError: (err) => {
        setConfirmPublish(false);
        const v = err?.response?.data?.validation;
        toast.error(
          v?.errors?.length
            ? `Publish blocked: ${v.errors[0]}`
            : err?.response?.data?.msg || 'Publish failed.',
        );
        refresh();
      },
    },
  );

  const editDefinition = (fn) => {
    setWorking((prev) => {
      const definition = cloneDefinition(prev.definition);
      fn(definition);
      return { ...prev, definition };
    });
    setDirty(true);
  };

  const editStep = (stepKey, fn) =>
    editDefinition((def) => {
      const step = def.steps.find((s) => s.stepKey === stepKey);
      if (step) fn(step);
    });

  const moveStep = (stepKey, delta) =>
    editDefinition((def) => {
      const index = def.steps.findIndex((s) => s.stepKey === stepKey);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= def.steps.length) return;
      const [step] = def.steps.splice(index, 1);
      def.steps.splice(target, 0, step);
      def.steps = normalizeChains(renumberSteps(def.steps));
    });

  const removeStep = (stepKey) =>
    editDefinition((def) => {
      def.steps = normalizeChains(
        renumberSteps(def.steps.filter((s) => s.stepKey !== stepKey)),
      );
    });

  const addStep = (day) => {
    // Step built from the CURRENT definition (not inside the updater) so the
    // expand call below sees the real key.
    const step = newStep(day, working?.definition?.steps || []);
    editDefinition((def) => {
      // Insert after the last step of that day (or at the end).
      let insertAt = def.steps.length;
      for (let i = def.steps.length - 1; i >= 0; i -= 1) {
        if (def.steps[i].day <= day) {
          insertAt = i + 1;
          break;
        }
        insertAt = i;
      }
      def.steps.splice(insertAt, 0, step);
      def.steps = normalizeChains(renumberSteps(def.steps));
    });
    setExpandedStep(step.stepKey);
  };

  const save = () =>
    saveMut.mutate({
      cadenceVersionId: serverVersion.cadenceVersionId,
      label: working.label,
      definition: working.definition,
    });

  const definition = working?.definition || { steps: [] };
  // The FULL day range renders (Nic 2026-08-09): quiet days (no touches —
  // 4, 8, 9 in the seed) show as manageable placeholders instead of
  // disappearing. Removing a day's last step turns it into a visible quiet
  // day; adding a step to a quiet day wakes it. The cadence length itself
  // stays fixed at 10 for now.
  const grouped = new Map(
    groupStepsByDay(definition.steps || []).map((g) => [g.day, g.steps]),
  );
  const lastDay = Math.max(10, ...grouped.keys(), 0);
  const dayGroups = Array.from({ length: lastDay }, (_, i) => ({
    day: i + 1,
    steps: grouped.get(i + 1) || [],
  }));
  const errorCount = validation.errors.length;
  // One UNPUBLISHED version at a time — a pending one blocks a new draft
  // just like an open draft does (the server enforces the same rule).
  const existingDraft = versions.find(
    (v) => v.status === 'draft' || v.status === 'pending_approval',
  );

  if (!open) return null;

  const body = (
    <>
      <div
        className={
          asPage
            ? 'flex h-[calc(100vh-150px)] min-h-[540px] flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] ring-1 ring-slate-200/70 dark:bg-surface-page dark:ring-white/10'
            : 'absolute inset-2 flex flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl dark:bg-surface-page md:inset-6'
        }
      >
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex min-w-0 items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
            <ListChecks size={17} className="shrink-0 text-slate-400" />
            <span className="truncate">Cadence editor</span>
          </div>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <span className="w-64">
              <PickerMenu
                label={
                  serverVersion
                    ? `v${serverVersion.versionNumber} · ${serverVersion.label}`
                    : 'Pick a version'
                }
                triggerClassName={`${CARD} px-3 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-200`}
                options={versions.map((v) => ({
                  value: v.cadenceVersionId,
                  // Auto version number + the human name (Nic 2026-08-09:
                  // names carry no version; numbers derive from creation
                  // order).
                  label: `v${v.versionNumber} · ${v.label}`,
                  // Right-aligned, never truncated — the label alone can be
                  // identical between the active version and its fresh
                  // drafts (Nic 2026-08-09 screenshot).
                  hint: STATUS_LABEL[v.status],
                }))}
                onPick={(id) => {
                  if (
                    dirty &&
                    !window.confirm('Discard unsaved changes on this draft?')
                  ) {
                    return;
                  }
                  setSelectedId(id);
                }}
              />
            </span>
            {/* ONE draft at a time (Nic 2026-08-09): hidden while the draft
                is the one on screen; GREYED OUT (not a jump button) while a
                draft exists and you're viewing the live/historical cadence
                — switching versions is the picker's job. */}
            {existingDraft &&
            existingDraft.cadenceVersionId ===
              serverVersion?.cadenceVersionId ? null : (
              <button
                type="button"
                disabled={
                  createMut.isLoading || !effectiveId || !!existingDraft
                }
                onClick={() =>
                  createMut.mutate(
                    effectiveId ? { sourceVersionId: effectiveId } : {},
                  )
                }
                title={
                  existingDraft?.status === 'pending_approval'
                    ? 'A version is waiting for approval'
                    : existingDraft
                      ? 'Draft already opened'
                      : "Start a new draft from the version you're viewing"
                }
                className={`${CARD} flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-800 disabled:opacity-40 dark:text-slate-300 dark:hover:text-slate-100`}
              >
                <Plus size={12} /> New draft
              </button>
            )}
            {editable ? (
              <>
                <button
                  type="button"
                  disabled={!dirty || saveMut.isLoading}
                  onClick={save}
                  className={`${CARD} px-3 py-1.5 text-[12px] font-semibold disabled:opacity-40 ${
                    dirty
                      ? 'text-slate-800 dark:text-slate-100'
                      : 'text-slate-400 dark:text-slate-500'
                  }`}
                >
                  {saveMut.isLoading ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </button>
                {/* Admins only (Nic 2026-10-23): whoever can approve
                    publishes straight away; Send for Approval is left for
                    someone who can edit but not approve (nobody, today). */}
                <button
                  type="button"
                  disabled={dirty || errorCount > 0 || submitMut.isLoading || publishMut.isLoading}
                  title={
                    dirty
                      ? 'Save first'
                      : errorCount > 0
                        ? 'Fix the validation errors first'
                        : undefined
                  }
                  onClick={() => setConfirmPublish(canApprove ? 'publish' : 'submit')}
                  className="flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:bg-slate-700 disabled:opacity-40 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-white"
                >
                  <UploadCloud size={12} />
                  {canApprove ? 'Publish' : 'Send for Approval'}
                  {errorCount > 0 ? (
                    <span className="rounded-full bg-rose-500 px-1.5 text-[11px]">
                      {errorCount}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  disabled={deleteMut.isLoading}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Delete this draft? This cannot be undone.',
                      )
                    )
                      deleteMut.mutate({
                        cadenceVersionId: serverVersion.cadenceVersionId,
                      });
                  }}
                  title="Delete draft"
                  className={`${CARD} p-2 text-rose-400 hover:text-rose-600 disabled:opacity-50`}
                >
                  <Trash2 size={12} />
                </button>
              </>
            ) : null}
            {/* Pending: withdraw belongs to the manager, approve to the
                approver. Someone with neither just sees a locked version. */}
            {pending && canEdit ? (
              <button
                type="button"
                disabled={withdrawMut.isLoading}
                onClick={() =>
                  withdrawMut.mutate({
                    cadenceVersionId: serverVersion.cadenceVersionId,
                  })
                }
                title="Pull this back to a draft so it can be edited"
                className={`${CARD} flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-800 disabled:opacity-40 dark:text-slate-300 dark:hover:text-slate-100`}
              >
                <Undo2 size={12} /> Withdraw
              </button>
            ) : null}
            {pending && canApprove ? (
              <button
                type="button"
                disabled={publishMut.isLoading}
                onClick={() => setConfirmPublish('publish')}
                className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-40"
              >
                <UploadCloud size={12} />
                Approve &amp; Publish
              </button>
            ) : null}
            {asPage ? null : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-200"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Unmissable mode indicator (Nic 2026-08-09, replaces the status
            pill): a thin full-bleed strip welded under the header, part of
            the frame rather than floating in the scroll area. */}
        {serverVersion?.status === 'draft' ? (
          <div className="flex shrink-0 items-center justify-center bg-gradient-to-r from-sky-500 via-sky-400 to-sky-500 px-4 py-[5px] text-[12px] font-semibold tracking-[0.02em] text-white dark:from-sky-600 dark:via-sky-500 dark:to-sky-600">
            Draft · not live until published
          </div>
        ) : null}
        {pending ? (
          <div className="flex shrink-0 items-center justify-center bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 px-4 py-[5px] text-[12px] font-semibold tracking-[0.02em] text-white dark:from-amber-600 dark:via-amber-500 dark:to-amber-600">
            Pending approval · locked
            {serverVersion?.submittedByName
              ? ` · sent by ${serverVersion.submittedByName}`
              : ''}
            {serverVersion?.submittedAt
              ? ` on ${new Date(serverVersion.submittedAt).toLocaleDateString()}`
              : ''}
          </div>
        ) : null}

        {/* Scrollable body. Capped rather than full-bleed (Nic 2026-08-14):
            5xl left the step rows and the scripts panel cramped, but an
            uncapped width would stretch step labels across an ultrawide and
            make the day columns unreadable. 7xl is the compromise. */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mx-auto max-w-7xl">
            {!editable && serverVersion ? (
              <div className="mb-3 rounded-lg bg-slate-100/70 px-3 py-1.5 text-[12px] font-medium text-slate-500 dark:bg-white/5 dark:text-slate-400">
                {serverVersion.status === 'active'
                  ? 'This is the live cadence. It is read-only: click New draft to make changes, then send them for approval.'
                  : serverVersion.status === 'historical'
                    ? 'A historical version, kept for the runs pinned to it. Read-only.'
                    : pending
                      ? canApprove
                        ? 'Waiting for your approval. Review the steps below, then Approve & Publish to make it live.'
                        : 'Sent for approval and locked. Withdraw it to make changes.'
                      : 'Read-only: you do not have permission to edit the cadence.'}
              </div>
            ) : null}

            {serverVersion ? (
              <IssuesBar validation={validation} dirty={dirty} />
            ) : null}

            {detailQ.isLoading || !working ? (
              <div className={`${CARD} p-6 text-center text-sm text-slate-400`}>
                Loading…
              </div>
            ) : (
              // The words moved to the Messages page (2026-10-22), so the
              // sidebar is just the version and a link there. minmax(0,1fr)
              // stops long step labels from forcing the grid wider.
              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="space-y-3">
                  {dayGroups.map(({ day, steps }) => (
                    <div key={day}>
                      <div className="mb-1.5 flex items-center gap-2 px-0.5">
                        <span className="text-[12px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          Day {day}
                        </span>
                        <span className="h-px flex-1 bg-slate-200/70 dark:bg-white/10" />
                        {editable ? (
                          <button
                            type="button"
                            onClick={() => addStep(day)}
                            className="flex items-center gap-0.5 text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                          >
                            <Plus size={12} /> Add step
                          </button>
                        ) : null}
                      </div>
                      <div className="space-y-1.5">
                        {(() => {
                          // Quiet day: no touches scheduled — visible and
                          // manageable (Nic 2026-08-09), not hidden. ml-4
                          // matches the spine gutter so it aligns with tiles.
                          if (steps.length === 0) {
                            return (
                              <div className="ml-4 flex items-center justify-center rounded-xl border border-dashed border-slate-200 py-3 text-[12px] font-medium text-slate-400 dark:!border-transparent dark:bg-white/5 dark:text-slate-500">
                                Quiet day, no touches.
                                {editable ? ' Use "Add step" to wake it.' : ''}
                              </div>
                            );
                          }
                          // Chain-group spine (Nic 2026-08-09): days with a
                          // timed sequence get a quiet connector in a slim
                          // left gutter — solid dot = re-times off the
                          // previous touch, hollow dot = gate-only, gray dot
                          // = the sequence's opening step. The gutter renders
                          // on EVERY row (empty on chain-free days) so all
                          // tiles start at the same x (Nic 2026-08-09).
                          const dayKeys = new Set(steps.map((s) => s.stepKey));
                          const chained = new Set();
                          steps.forEach((s) => {
                            if (s.chainAfter && dayKeys.has(s.chainAfter)) {
                              chained.add(s.stepKey);
                              chained.add(s.chainAfter);
                            }
                          });
                          return steps.map((step, i) => {
                            const inChain = chained.has(step.stepKey);
                            const upLink =
                              inChain &&
                              i > 0 &&
                              chained.has(steps[i - 1].stepKey);
                            const downLink =
                              inChain &&
                              i < steps.length - 1 &&
                              chained.has(steps[i + 1].stepKey);
                            const reTimed =
                              step.chainOffsetStartMinutes !== undefined;
                            const row = (
                              <StepRow
                                key={step.stepKey}
                                step={step}
                                steps={definition.steps}
                                scripts={liveScripts}
                                editable={editable}
                                expanded={expandedStep === step.stepKey}
                                onToggle={() =>
                                  setExpandedStep(
                                    expandedStep === step.stepKey
                                      ? null
                                      : step.stepKey,
                                  )
                                }
                                onChange={(fn) => editStep(step.stepKey, fn)}
                                onMove={(delta) =>
                                  moveStep(step.stepKey, delta)
                                }
                                onRemove={() => removeStep(step.stepKey)}
                              />
                            );
                            return (
                              <div
                                key={step.stepKey}
                                className="flex items-stretch"
                              >
                                <div
                                  className="relative w-4 shrink-0"
                                  title={
                                    inChain
                                      ? 'Part of this day’s timed sequence'
                                      : undefined
                                  }
                                >
                                  {upLink ? (
                                    <span
                                      className="absolute -top-1.5 left-1/2 w-px -translate-x-1/2 bg-sky-300 dark:bg-sky-500/50"
                                      style={{
                                        height: 'calc(20px + 0.375rem)',
                                      }}
                                    />
                                  ) : null}
                                  {downLink ? (
                                    <span className="absolute -bottom-1.5 left-1/2 top-[20px] w-px -translate-x-1/2 bg-sky-300 dark:bg-sky-500/50" />
                                  ) : null}
                                  {inChain ? (
                                    <span
                                      className={`absolute left-1/2 top-[20px] h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                                        !step.chainAfter
                                          ? 'bg-slate-300 dark:bg-slate-600'
                                          : reTimed
                                            ? 'bg-sky-400'
                                            : 'border-[1.5px] border-sky-400 bg-white dark:!bg-surface-card'
                                      }`}
                                    />
                                  ) : null}
                                </div>
                                <div className="min-w-0 flex-1">{row}</div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <VersionCard
                    label={working.label}
                    versionNumber={serverVersion?.versionNumber}
                    timezone={serverVersion?.timezone || 'America/Chicago'}
                    editable={editable}
                    onLabel={(label) => {
                      setWorking((prev) => ({ ...prev, label }));
                      setDirty(true);
                    }}
                  />
                  <MessagesCard />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmPublish && serverVersion ? (
        <PublishConfirm
          mode={confirmPublish}
          label={working?.label || serverVersion.label}
          diff={diffDefinitions(
            activeDetailQ.data?.version?.definition,
            definition,
          )}
          busy={
            confirmPublish === 'submit'
              ? submitMut.isLoading
              : publishMut.isLoading
          }
          onConfirm={() =>
            (confirmPublish === 'submit' ? submitMut : publishMut).mutate({
              cadenceVersionId: serverVersion.cadenceVersionId,
            })
          }
          onCancel={() => setConfirmPublish(false)}
        />
      ) : null}
    </>
  );
  if (asPage) return body;
  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      {body}
    </div>,
    document.body,
  );
};

export default CadenceEditorOverlay;
